<script lang="ts">
    import {
        ALL_TOOTH_FDI,
        archForFdi,
        cycleStageValue,
        addToothValue,
        removeToothValue,
        taggedTeeth,
        markedSurfacesForTooth,
        toothDisplayColor,
        getToothNote,
        setToothNote,
        createDefaultOdontogramaValue,
        type OdontogramaValue,
        type OdontoStage,
        type ToothSurface,
    } from './odontograma-data'

    export let stages: OdontoStage[] = []
    export let value: OdontogramaValue = createDefaultOdontogramaValue()

    let query = ''
    let activeFdi: string | null = null
    let queryInput: HTMLInputElement
    let suggestionsOpen = false

    // Buffer local da anotação do dente ativo — inicializado só quando o dente ativo MUDA (nunca
    // re-sincronizado a partir de `value` a cada tecla, senão o cursor pula/reformata; mesmo
    // padrão já usado em choice-one-view.svelte pro campo "Outro").
    let noteText = ''
    let noteFdi: string | null = null
    $: if (activeFdi !== noteFdi) {
        noteFdi = activeFdi
        noteText = activeFdi ? getToothNote(value, activeFdi) : ''
    }

    // A lista de tags é derivada de `value` (sem estado próprio) — marcar/remover em qualquer um
    // dos dois odontogramas (gráfico ou esta lista) já reflete no outro, porque os dois só leem e
    // escrevem o mesmo `value`.
    $: tagged = taggedTeeth(value)
    $: suggestions = query.trim()
        ? ALL_TOOTH_FDI.filter((fdi) => fdi.startsWith(query.trim()) && !tagged.includes(fdi)).slice(0, 8)
        : []

    // As 4 faces fixas + a 5ª (L/P) resolvida pela arcada do dente ativo — mesma regra de
    // `surfaceFor` no odontograma gráfico (superior -> Palatal, inferior -> Lingual), só que aqui
    // o rótulo mostra as duas letras porque não há um desenho pra deixar óbvio qual se aplica.
    // "O/I" sempre grava como 'O': o odontograma gráfico não tem uma região clicável de incisal
    // separada (dentes anteriores não têm "centro"), então aqui usamos a mesma chave pros dois
    // casos (oclusal em posteriores, incisal em anteriores).
    //
    // Sempre tem os 5 itens, mesmo sem dente ativo (nesse caso os toggles ficam desabilitados,
    // funcionando como placeholder — ver markup — em vez de sumir e deixar um buraco no layout;
    // a resolução L/P sem dente ativo é arbitrária, não importa porque fica desabilitado).
    $: faceToggles = [
        { surface: 'M', label: 'M' },
        { surface: 'O', label: 'O/I' },
        { surface: 'D', label: 'D' },
        { surface: 'V', label: 'V' },
        { surface: activeFdi && archForFdi(activeFdi) === 'lower' ? 'L' : 'P', label: 'L/P' },
    ] as { surface: ToothSurface; label: string }[]

    function addTooth(fdi: string) {
        value = addToothValue(value, fdi)
        query = ''
        suggestionsOpen = false
        activeFdi = fdi
        queryInput?.focus()
    }

    function handleQueryKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter' && suggestions.length > 0) {
            event.preventDefault()
            addTooth(suggestions[0])
        } else if (event.key === 'Escape') {
            suggestionsOpen = false
        }
    }

    // Delay pra permitir o `mousedown` na sugestão rodar antes do input perder foco (senão a lista
    // some antes do clique registrar).
    function handleQueryBlur() {
        setTimeout(() => (suggestionsOpen = false), 150)
    }

    function removeTooth(fdi: string) {
        value = removeToothValue(value, fdi)
        if (activeFdi === fdi) activeFdi = null
    }

    function selectTooth(fdi: string) {
        activeFdi = activeFdi === fdi ? null : fdi
    }

    function toggleFace(surface: ToothSurface) {
        if (!activeFdi) return
        value = cycleStageValue(value, stages, `${activeFdi}:${surface}`)
    }

    function onNoteInput() {
        if (!activeFdi) return
        value = setToothNote(value, activeFdi, noteText)
    }
</script>

<div class="tag-box">
    <span class="tag-box-label">Selecionar Dente/Região</span>
    <div class="tag-field">
        {#each tagged as fdi (fdi)}
            {@const marked = markedSurfacesForTooth(value, fdi)}
            {@const color = toothDisplayColor(value, stages, fdi)}
            <span class="tag-pill" class:active={activeFdi === fdi} style:background-color={color}>
                <button type="button" class="tag-pill-body" on:click={() => selectTooth(fdi)}>
                    {fdi}{marked.length > 0 ? ` (${marked.join(', ')})` : ''}
                </button>
                <button type="button" class="tag-pill-remove" aria-label="Remover dente {fdi}" on:click={() => removeTooth(fdi)}>
                    &times;
                </button>
            </span>
        {/each}
        <input
            type="text"
            class="tag-query"
            placeholder="Inserir dente..."
            bind:value={query}
            bind:this={queryInput}
            on:input={() => (suggestionsOpen = true)}
            on:focus={() => (suggestionsOpen = true)}
            on:blur={handleQueryBlur}
            on:keydown={handleQueryKeydown}
        />
    </div>
    {#if suggestionsOpen && suggestions.length > 0}
        <ul class="tag-suggestions">
            {#each suggestions as fdi (fdi)}
                <li>
                    <button type="button" on:mousedown|preventDefault={() => addTooth(fdi)}>{fdi}</button>
                </li>
            {/each}
        </ul>
    {/if}
</div>

<div class="detail-row">
    <div class="face-box">
        <span class="face-box-label">Face{activeFdi ? ` - Dente ${activeFdi}` : ''}</span>
        <div class="face-toggles">
            {#each faceToggles as toggle (toggle.label)}
                {@const label = activeFdi ? value[`${activeFdi}:${toggle.surface}`] : undefined}
                {@const color = stages.find((s) => s.label === label)?.color ?? null}
                <button type="button" class="face-toggle" disabled={!activeFdi} on:click={() => toggleFace(toggle.surface)}>
                    <span class="face-check" class:checked={color != null} style:background-color={color} style:border-color={color}></span>
                    {toggle.label}
                </button>
            {/each}
        </div>
    </div>

    <div class="notes-box">
        <label class="notes-label" for="odonto-note">Anotação{activeFdi ? ` - Dente ${activeFdi}` : ''}</label>
        <textarea
            id="odonto-note"
            class="notes-textarea"
            rows="2"
            placeholder={activeFdi ? `Adicionar observação sobre o dente ${activeFdi}...` : 'Selecione um dente na lista acima para adicionar uma anotação'}
            disabled={!activeFdi}
            bind:value={noteText}
            on:input={onNoteInput}
        ></textarea>
    </div>
</div>

<style>
    .tag-box {
        position: relative;
        width: 100%;
        border: 1px solid #ced4da;
        border-radius: 10px;
        padding: 10px 12px;
        display: flex;
        flex-direction: column;
        gap: 6px;
        box-sizing: border-box;
    }
    .tag-box-label {
        font-size: 0.75rem;
        font-weight: 600;
        color: #868e96;
    }
    .tag-field {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
    }
    /* O fundo é a cor calculada do dente (ver toothDisplayColor) — cinza sem face marcada, ou a
       cor da face de maior prioridade. "Ativo" (sendo editado agora) vira um anel ao redor em vez
       de trocar o fundo, pra não esconder essa cor. */
    .tag-pill {
        display: inline-flex;
        align-items: stretch;
        border-radius: 999px;
        background: #e9ecef;
        overflow: hidden;
    }
    .tag-pill.active {
        box-shadow: 0 0 0 2px var(--cs2-accent, #1bdf6f);
    }
    .tag-pill-body {
        border: none;
        background: transparent;
        padding: 4px 4px 4px 12px;
        font-size: 0.85rem;
        font-weight: 600;
        color: #212529;
        cursor: pointer;
        white-space: nowrap;
    }
    .tag-pill-remove {
        border: none;
        background: transparent;
        padding: 4px 10px 4px 4px;
        font-size: 0.95rem;
        line-height: 1;
        color: #495057;
        cursor: pointer;
    }
    .tag-pill-remove:hover {
        opacity: 0.7;
    }
    .tag-query {
        flex: 1 1 120px;
        min-width: 100px;
        border: none;
        outline: none;
        font-size: 0.85rem;
        padding: 4px;
        color: #212529;
    }
    .tag-suggestions {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        z-index: 10;
        margin: 4px 0 0;
        padding: 4px;
        list-style: none;
        background: #fff;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        max-height: 220px;
        overflow-y: auto;
    }
    .tag-suggestions li button {
        display: block;
        width: 100%;
        text-align: left;
        border: none;
        background: transparent;
        padding: 6px 10px;
        font-size: 0.85rem;
        border-radius: 6px;
        cursor: pointer;
        color: #212529;
    }
    .tag-suggestions li button:hover {
        background: #f1f3f5;
    }

    /* Linha de baixo: face à esquerda, anotação à direita. `min-height` nos dois é o mesmo valor
       (calibrado contra o conteúdo real de cada um com um dente ativo) — reserva o espaço mesmo
       sem dente selecionado, pra selecionar/desselecionar não pular o layout dos blocos abaixo. */
    .detail-row {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        align-items: stretch;
    }
    .face-box {
        flex: 0 1 260px;
        min-height: 141px;
        padding: 10px 12px;
        box-sizing: border-box;
    }
    .face-box-label {
        display: block;
        font-size: 0.85rem;
        font-weight: 700;
        color: #212529;
        margin-bottom: 8px;
    }
    .face-toggles {
        display: grid;
        grid-template-columns: repeat(3, minmax(70px, auto));
        gap: 8px 16px;
    }
    .face-toggle {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        border: none;
        background: transparent;
        padding: 0;
        font-size: 0.85rem;
        font-weight: 600;
        color: #212529;
        cursor: pointer;
    }
    /* Sem dente ativo, os 5 toggles continuam visíveis (mesmo grid, mesmo espaço) só que
       desabilitados — funcionam como placeholder em vez de sumir e deixar um buraco no layout. */
    .face-toggle:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
    .face-check {
        width: 16px;
        height: 16px;
        border-radius: 4px;
        border: 1.5px solid #adb5bd;
        background: #fff;
        flex-shrink: 0;
    }
    .face-check.checked {
        border-style: solid;
    }

    .notes-box {
        flex: 1 1;
        min-width: 260px;
        min-height: 141px;
        padding: 10px 12px;
        display: flex;
        flex-direction: column;
        gap: 6px;
        box-sizing: border-box;
    }
    .notes-label {
        font-size: 0.85rem;
        font-weight: 700;
        color: #212529;
    }
    .notes-textarea {
        width: 100%;
        resize: vertical;
        border: 1px solid #ced4da;
        border-radius: 10px;
        padding: 10px 12px;
        font-size: 0.85rem;
        font-family: inherit;
        color: #212529;
    }
    .notes-textarea:focus {
        outline: none;
        border-color: var(--cs2-accent, #1bdf6f);
    }
    .notes-textarea:disabled {
        cursor: not-allowed;
        background: #f8f9fa;
        color: #adb5bd;
    }
</style>
